'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import type { Workspace } from '@/contexts/WorkspaceContext'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { GuestAuthDialog } from '@/components/GuestAuthDialog'
import { cn } from '@/lib/utils'
import {
  sidebarRailFooterLabelClass,
  sidebarRailFooterTriggerClass,
} from '@/components/sidebarRailFooterTrigger'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon, Settings01Icon } from '@hugeicons-pro/core-solid-rounded'
import { PlusSignIcon as PlusSignStrokeIcon, Tick02Icon, UserAdd01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { createClient } from '@/utils/supabase/client'
import { Progress } from '@/components/ui/progress'

const WORKSPACE_AVATAR_BASE = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/workspace-avatars`
const CREDIT_RING_SIZE = 32
const CREDIT_RING_STROKE = 2.5
const CREDIT_RING_RADIUS = (CREDIT_RING_SIZE - CREDIT_RING_STROKE) / 2
const CREDIT_RING_CIRCUMFERENCE = 2 * Math.PI * CREDIT_RING_RADIUS

function formatPlanLabel(tier: string) {
  if (!tier) return 'Plan'
  return tier
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

function formatPercentage(value: number | null) {
  if (value === null) return null
  return `${Math.round(value)}%`
}

function formatCredits(value: number) {
  return value.toLocaleString()
}

function WorkspaceCreditsAvatar({
  imageSrc,
  usagePercent,
  impressionsRemaining,
  impressionsTotal,
}: {
  imageSrc?: string
  usagePercent: number | null
  impressionsRemaining?: number
  impressionsTotal?: number
}) {
  const pct = usagePercent === null ? 0 : Math.max(0, Math.min(100, usagePercent))
  const dashOffset = CREDIT_RING_CIRCUMFERENCE * (1 - pct / 100)
  const percentLabel = formatPercentage(pct)

  const ratio = usagePercent === null ? null : pct / 100
  const strokeColorClass = ratio === null
    ? 'stroke-atrium-border'
    : ratio > 0.5
    ? 'stroke-emerald-500'
    : ratio > 0.25
    ? 'stroke-yellow-500'
    : 'stroke-red-500'

  const tooltipText = impressionsTotal && impressionsTotal > 0
    ? `${(impressionsRemaining ?? 0).toLocaleString()} / ${impressionsTotal.toLocaleString()} impressions remaining`
    : null

  return (
    <div className="relative size-8 shrink-0" title={tooltipText ?? undefined}>
      <svg
        aria-hidden
        viewBox={`0 0 ${CREDIT_RING_SIZE} ${CREDIT_RING_SIZE}`}
        className="-rotate-90 size-8"
      >
        <circle
          cx={CREDIT_RING_SIZE / 2}
          cy={CREDIT_RING_SIZE / 2}
          r={CREDIT_RING_RADIUS}
          fill="none"
          className="stroke-atrium-border/80"
          strokeWidth={CREDIT_RING_STROKE}
        />
        <circle
          cx={CREDIT_RING_SIZE / 2}
          cy={CREDIT_RING_SIZE / 2}
          r={CREDIT_RING_RADIUS}
          fill="none"
          className={cn(strokeColorClass, 'transition-[stroke-dashoffset] duration-300 ease-out')}
          strokeWidth={CREDIT_RING_STROKE}
          strokeDasharray={CREDIT_RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-[4px] overflow-hidden rounded-full">
        <Avatar className="size-full rounded-full">
          <AvatarImage src={imageSrc} className="object-cover" />
          <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-accent-600 text-transparent">
            {'\u200b'}
          </AvatarFallback>
        </Avatar>
        {percentLabel ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-atrium-border/95 text-[9px] font-medium text-black dark:text-white opacity-0 transition-opacity duration-150 group-hover/workspace-trigger:opacity-100">
            {percentLabel}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export type AppSidebarWorkspaceMenuProps = {
  expanded: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  currentWorkspace: Workspace | null
  workspaces: Workspace[]
  workspaceRoles: Record<string, string>
  workspacesLoading: boolean
  handleWorkspaceChange: (id: string) => void
  isDesktop: boolean
  setShowCreateForm: (open: boolean) => void
  setWorkspaceDrawerView: (v: 'list' | 'create') => void
  setWorkspacePopoverOpenMobile: (open: boolean) => void
  onOpenWorkspaceSettings: (section?: string) => void
}

export function AppSidebarWorkspaceMenu(props: AppSidebarWorkspaceMenuProps) {
  const {
    expanded,
    open,
    onOpenChange,
    currentWorkspace,
    workspaces,
    workspaceRoles,
    workspacesLoading,
    handleWorkspaceChange,
    isDesktop,
    setShowCreateForm,
    setWorkspaceDrawerView,
    setWorkspacePopoverOpenMobile,
    onOpenWorkspaceSettings,
  } = props

  const compact = !expanded
  const loading = workspacesLoading
  const isGuest = !currentWorkspace?.id && workspaces.length === 0
  const name = !workspacesLoading ? currentWorkspace?.display_name?.trim() ?? null : null
  const triggerAria = name ? `Workspace: ${name}` : 'Workspace'
  const imageSrc = currentWorkspace?.avatar_path ? `${WORKSPACE_AVATAR_BASE}/${currentWorkspace.avatar_path}` : undefined

  const [showGuestAuth, setShowGuestAuth] = useState(false)
  const [memberCount, setMemberCount] = useState<number | null>(null)
  const [creditUsagePct, setCreditUsagePct] = useState<number | null>(null)
  const [creditBalance, setCreditBalance] = useState<number | null>(null)
  const [lifetimePurchased, setLifetimePurchased] = useState<number>(0)
  const [focusedIndex, setFocusedIndex] = useState(-1)

  const handleGuestAction = () => {
    onOpenChange(false)
    setShowGuestAuth(true)
  }

  useEffect(() => {
    if (open) setFocusedIndex(-1)
  }, [open])

  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({})

  useEffect(() => {
    if (!currentWorkspace?.id) {
      setCreditUsagePct(null)
      setCreditBalance(null)
      return
    }
    let cancelled = false
    const supabase = createClient()

    const refreshCreditUsage = async () => {
      const { data: credits } = await supabase
        .from('workspace_credits' as any)
        .select('balance, lifetime_purchased')
        .eq('workspace_id', currentWorkspace.id)
        .maybeSingle()

      if (cancelled) return

      const remaining = Math.max(0, Number((credits as any)?.balance ?? 0))
      const purchased = Number((credits as any)?.lifetime_purchased ?? 0)
      setCreditBalance(remaining)
      setLifetimePurchased(purchased)
      if (purchased <= 0) {
        setCreditUsagePct(null)
        return
      }

      const ratio = (remaining / purchased) * 100
      setCreditUsagePct(Math.max(0, Math.min(100, ratio)))
    }

    void refreshCreditUsage()

    return () => {
      cancelled = true
    }
  }, [currentWorkspace?.id])

  const remainingCredits = creditBalance ?? 0
  const remainingCreditPct =
    creditUsagePct === null ? (lifetimePurchased > 0 ? 0 : null) : Math.max(0, Math.min(100, creditUsagePct))

  useEffect(() => {
    if (!open || !currentWorkspace?.id) {
      setMemberCount(null)
      return
    }
    let cancelled = false
    const supabase = createClient()
    // Fetch member counts for ALL workspaces
    void Promise.all(
      workspaces.map(async (ws) => {
        const { count } = await supabase
          .from('workspace_members')
          .select('*', { count: 'exact', head: true })
          .eq('workspace_id', ws.id)
        return { id: ws.id, count: count ?? 0 }
      })
    ).then((results) => {
      if (cancelled) return
      const counts: Record<string, number> = {}
      for (const r of results) counts[r.id] = r.count
      setMemberCounts(counts)
      setMemberCount(counts[currentWorkspace.id] ?? 0)
    })
    return () => {
      cancelled = true
    }
  }, [open, currentWorkspace?.id, workspaces])

  const openCreateWorkspace = () => {
    onOpenChange(false)
    if (isDesktop) {
      setShowCreateForm(true)
    } else {
      setWorkspaceDrawerView('create')
      setWorkspacePopoverOpenMobile(true)
    }
  }

  const goSettings = () => {
    onOpenChange(false)
    onOpenWorkspaceSettings()
  }

  const goInviteTeammates = () => {
    onOpenChange(false)
    onOpenWorkspaceSettings('team')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!workspaces.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex(prev => (prev + 1) % workspaces.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex(prev => (prev - 1 + workspaces.length) % workspaces.length)
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault()
      handleWorkspaceChange(workspaces[focusedIndex].id)
      onOpenChange(false)
    } else if (e.key === 'Escape') {
      onOpenChange(false)
    }
  }

  const triggerInner = (
    <>
      {loading ? (
        <>
          <div className="flex min-w-0 flex-1 items-center gap-2 px-1">
            <div className="relative size-8 shrink-0" aria-hidden>
              <Skeleton className="size-8 rounded-full bg-atrium-border/60" />
            </div>
            <span aria-hidden={compact || undefined} className={sidebarRailFooterLabelClass(compact)}>
              <Skeleton className="h-3.5 w-28 max-w-full rounded bg-atrium-border/60" />
            </span>
          </div>
          {!compact ? (
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              className="size-3.5 shrink-0 text-atrium-black/40"
              aria-hidden
            />
          ) : null}
        </>
      ) : (
        <>
          <div className="flex min-w-0 flex-1 items-center gap-2 px-1">
            <WorkspaceCreditsAvatar imageSrc={imageSrc} usagePercent={creditUsagePct} impressionsRemaining={remainingCredits} impressionsTotal={lifetimePurchased} />
            <span aria-hidden={compact || undefined} className={sidebarRailFooterLabelClass(compact)}>
              <span className="flex min-w-0 text-left">
                {name ? (
                  <span className="truncate text-[13px] font-medium text-atrium-black">{name}</span>
                ) : (
                  <Skeleton className="h-3.5 w-28 rounded bg-atrium-border/60" />
                )}
              </span>
            </span>
          </div>
          {/* {!compact ? (
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              className={cn(
                'size-3.5 shrink-0 text-atrium-black transition-transform duration-200 ease-out motion-reduce:transition-none -ml-4',
                open && 'rotate-180'
              )}
              aria-hidden
            />
          ) : null} */}
        </>
      )}
    </>
  )

  const triggerButton = (
    <button
      type="button"
      aria-label={triggerAria}
      aria-haspopup="dialog"
      aria-expanded={open}
      className={cn(
        sidebarRailFooterTriggerClass({ pressed: open }),
        'group/workspace-trigger mt-2 h-9 w-full shrink-0 gap-2 hover:bg-transparent focus:bg-transparent active:bg-transparent'
      )}
    >
      {triggerInner}
    </button>
  )

  return (
    <div className="flex w-full min-w-0 shrink-0 flex-col">
      <Popover open={open} onOpenChange={onOpenChange}>
        {compact ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">
              {name ? (
                <span className="max-w-[220px] font-normal">{name}</span>
              ) : (
                <span className="font-normal text-[#939294]">Workspace</span>
              )}
            </TooltipContent>
          </Tooltip>
        ) : (
          <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        )}
        <PopoverContent
          className="z-[130] w-[min(calc(100vw-1.5rem),20rem)] rounded-xl border border-atrium-border bg-popover p-0 text-popover-foreground shadow-lg ml-2"
          align="start"
          side="bottom"
          sideOffset={8}
          onPointerDownOutside={(e) => e.stopPropagation()}
          onInteractOutside={(e) => e.stopPropagation()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onKeyDown={handleKeyDown}
        >
          {currentWorkspace ? (
            <div className="border-b border-atrium-border px-4 pb-3 pt-3">
              <div className="flex gap-3">
                <Avatar className="size-10 shrink-0 overflow-hidden rounded-full">
                  <AvatarImage
                    src={
                      currentWorkspace.avatar_path
                        ? `${WORKSPACE_AVATAR_BASE}/${currentWorkspace.avatar_path}`
                        : undefined
                    }
                    className="object-cover"
                  />
                  <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-accent-600 text-transparent">
                    {'\u200b'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-normal text-atrium-black">{currentWorkspace.display_name}</p>
                  {isGuest ? (
                    <p className="mt-0.5 text-xs text-atrium-black/55">Guest</p>
                  ) : memberCount === null ? (
                    <Skeleton className="mt-1 h-3 w-36 rounded bg-atrium-border/60" />
                  ) : (
                    <p className="mt-0.5 text-xs text-atrium-black/55">
                      {formatPlanLabel(currentWorkspace.plan_tier)} · {memberCount}{' '}
                      {memberCount === 1 ? 'member' : 'members'}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {isGuest ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGuestAction}
                      className="h-9 w-full gap-1 border-atrium-border px-2 text-xs font-normal opacity-60"
                    >
                      <HugeiconsIcon icon={Settings01Icon} className="size-3 shrink-0" />
                      Settings
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGuestAction}
                      className="h-9 w-full gap-1 border-atrium-border px-2 text-xs font-normal opacity-60"
                    >
                      <HugeiconsIcon icon={UserAdd01Icon} className="size-3 shrink-0" />
                      Invite teammates
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 gap-1 border-atrium-border px-2 text-xs font-normal"
                      onClick={goSettings}
                    >
                      <HugeiconsIcon icon={Settings01Icon} className="size-3 shrink-0" />
                      Settings
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 gap-1 border-atrium-border px-2 text-xs font-normal"
                      onClick={goInviteTeammates}
                    >
                      <HugeiconsIcon icon={UserAdd01Icon} className="size-3 shrink-0" />
                      Invite teammates
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {currentWorkspace ? (
            <div className="border-b border-atrium-border px-4 py-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-atrium-black/45">
                  Impressions remaining
                </p>
                <Link
                  href="/usage"
                  onClick={() => onOpenChange(false)}
                  className="text-xs font-medium text-accent-600 hover:text-accent-700"
                >
                  View usage
                </Link>
              </div>
              {remainingCreditPct !== null ? (
                <Progress
                  value={remainingCreditPct}
                  className="h-2 bg-atrium-border/60 [&>div]:bg-accent-600"
                  aria-label="Remaining credits"
                />
              ) : (
                <div className="h-2 w-full rounded-full bg-atrium-border/60" aria-hidden />
              )}
              <p className="mt-2 text-xs text-atrium-black/60">
                {formatCredits(remainingCredits)} / {formatCredits(lifetimePurchased)} impressions
              </p>
            </div>
          ) : null}

          {!isGuest && (
            <div className="px-3 pt-3">
              <p className="px-1 pb-2 text-[11px] font-medium uppercase tracking-wide text-atrium-black/45">
                All workspaces
              </p>
              <div className="max-h-52 overflow-y-auto pb-2">
                {workspacesLoading ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-2">
                      <Skeleton className="h-7 w-7 rounded-md bg-atrium-border/60" />
                      <Skeleton className="h-4 flex-1 bg-atrium-border/60" />
                    </div>
                  ))
                ) : (
                  workspaces.map((org, index) => (
                    <button
                      key={org.id}
                      type="button"
                      onClick={() => {
                        handleWorkspaceChange(org.id)
                        onOpenChange(false)
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-normal text-atrium-black transition-colors hover:bg-atrium-content',
                        currentWorkspace?.id === org.id && 'bg-atrium-background',
                        focusedIndex === index && 'ring-1 ring-inset ring-accent-600/30 bg-accent-600/10'
                      )}
                    >
                      <Avatar className="size-8 shrink-0 overflow-hidden rounded-full">
                        <AvatarImage
                          src={org.avatar_path ? `${WORKSPACE_AVATAR_BASE}/${org.avatar_path}` : undefined}
                          className="object-cover"
                        />
                        <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-accent-600 text-transparent">
                          {'\u200b'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <span className="block truncate text-sm">{org.display_name}</span>
                        <span className="block text-[11px] text-atrium-black/40">
                          {memberCounts[org.id] != null ? `${memberCounts[org.id]} member${memberCounts[org.id] !== 1 ? 's' : ''}` : ''}
                          {workspaceRoles[org.id] && memberCounts[org.id] != null ? ' · ' : ''}
                          {workspaceRoles[org.id] ? ({'owner':'Owner','admin':'Admin','member':'Member','viewer':'Viewer'}[workspaceRoles[org.id]] || workspaceRoles[org.id]) : ''}
                        </span>
                      </div>
                      {currentWorkspace?.id === org.id && <HugeiconsIcon icon={Tick02Icon} className="h-3.5 w-3.5 shrink-0 text-accent-600" />}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="border-t border-atrium-border p-3">
            {isGuest ? (
              <Button
                type="button"
                variant="ghost"
                onClick={handleGuestAction}
                className="h-11 w-full opacity-60"
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-black/10">
                  <HugeiconsIcon icon={PlusSignStrokeIcon} className="size-3.5" />
                </span>
                New workspace
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="h-11 w-full"
                onClick={openCreateWorkspace}
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-black/10">
                  <HugeiconsIcon icon={PlusSignStrokeIcon} className="size-3.5" />
                </span>
                Create new workspace
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <GuestAuthDialog
        open={showGuestAuth}
        onOpenChange={setShowGuestAuth}
        heading="Sign in to Atrium"
        body="Create a free account or sign in to access workspace settings, invite teammates, and more."
      />
    </div>
  )
}
